import React from 'react';

const shimmer: React.CSSProperties = {
  background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 2s infinite',
};

const cardShell: React.CSSProperties = {
  background: 'white',
  borderRadius: 16,
  boxShadow: '0 2px 10px rgba(0,0,0,.06)',
  border: '1px solid #e5e7eb',
};

const ClassAttendanceSkeletonLoader: React.FC = () => {
  return (
    <>
      {/* Top row: calendar + status bars + pie */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 16,
          alignItems: 'stretch',
          flexWrap: 'wrap',
        }}
      >
        {/* Calendar placeholder */}
        <div style={{ ...cardShell, padding: 14, flex: '0 0 auto', width: 280 }}>
          <div style={{ width: '60%', height: 14, borderRadius: 6, marginBottom: 14, ...shimmer }} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 6,
            }}
          >
            {[...Array(35)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: '100%',
                  aspectRatio: '1',
                  borderRadius: 6,
                  ...shimmer,
                }}
              />
            ))}
          </div>
        </div>

        {/* Status bars card */}
        <div
          style={{
            ...cardShell,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 320px',
            minWidth: 260,
            gap: 14,
          }}
        >
          <div style={{ width: 120, height: 14, borderRadius: 6, ...shimmer }} />
          {[...Array(5)].map((_, i) => (
            <div key={i}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}
              >
                <div style={{ width: 70, height: 12, borderRadius: 6, ...shimmer }} />
                <div style={{ width: 28, height: 12, borderRadius: 6, ...shimmer }} />
              </div>
              <div
                style={{
                  height: 10,
                  borderRadius: 999,
                  width: `${90 - i * 12}%`,
                  ...shimmer,
                }}
              />
            </div>
          ))}
        </div>

        {/* Pie chart card */}
        <div
          style={{
            ...cardShell,
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 276px',
            minWidth: 253,
            maxWidth: 322,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: 140,
              height: 14,
              borderRadius: 6,
              marginBottom: 20,
              alignSelf: 'flex-start',
              ...shimmer,
            }}
          />
          <div
            style={{
              width: 160,
              height: 160,
              borderRadius: '50%',
              ...shimmer,
            }}
          />
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 20,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{ width: 60, height: 12, borderRadius: 6, ...shimmer }} />
            ))}
          </div>
        </div>
      </div>

      {/* Student list table */}
      <div
        style={{
          background: 'white',
          borderRadius: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,.06)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            background: '#f9fafb',
            display: 'flex',
            gap: 16,
            alignItems: 'center',
          }}
        >
          {[40, 180, 70, 60, 100, 140].map((w, i) => (
            <div
              key={i}
              style={{
                width: w,
                height: 11,
                borderRadius: 6,
                flexShrink: 0,
                ...shimmer,
              }}
            />
          ))}
        </div>

        {/* Rows — # · Student · Gender · Roll · Status · Remarks */}
        <div style={{ padding: '4px 0' }}>
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                padding: '16px 20px',
                borderBottom: index < 7 ? '1px solid #f3f4f6' : 'none',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${index * 0.08}s`,
              }}
            >
              {/* Serial */}
              <div style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0, ...shimmer }} />

              {/* Student (avatar + name + admission) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0, ...shimmer }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ width: '70%', height: 14, borderRadius: 8, marginBottom: 6, ...shimmer }} />
                  <div style={{ width: '45%', height: 11, borderRadius: 8, ...shimmer }} />
                </div>
              </div>

              {/* Gender */}
              <div style={{ width: 60, height: 14, borderRadius: 8, flexShrink: 0, ...shimmer }} />

              {/* Roll No */}
              <div style={{ width: 40, height: 24, borderRadius: 6, flexShrink: 0, ...shimmer }} />

              {/* Status pill */}
              <div style={{ width: 90, height: 26, borderRadius: 999, flexShrink: 0, ...shimmer }} />

              {/* Remarks */}
              <div style={{ width: 120, height: 14, borderRadius: 8, flexShrink: 0, ...shimmer }} />
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
};

export default ClassAttendanceSkeletonLoader;